import { useTranslation } from "react-i18next";
import { AppPopover } from "../../../../common/AppPopover";
import { usePopover } from "../../../../common/hooks/use-popover";
import type { TStoreData } from "../../../../services/api/instances/vectorStoreApi/types";
import { AppIconButton } from "../../../../shared/components/AppIconButton";
import { AppLoading } from "../../../../shared/components/AppLoading";
import { AppUnderlinedTitle } from "../../../../shared/components/AppUnderlinedTitle";
import { PaperclipIcon } from "../../../../shared/icons/PaperclipIcon";
import { PlusIcon } from "../../../../shared/icons/PlusIcon";
import { TrashIcon } from "../../../../shared/icons/TrashIcon";
import { useAddDatabaseFile } from "../../hooks/use-add-database-file";
import { AttachedFileMenu } from "../../../../common/Chat/Components/AttachedFileMenu";
import { useMenu } from "../../../../common/hooks/use-menu";
import { UPLOADED_FILE_EXTENSIONS } from "../../../Modals/AttachedFileModal/constants";
import { AppButton } from "../../../../shared/components/AppButton";
import { AttachedFileToDatabaseModal } from "../../../Modals/AttachedFileToDatabaseModal";
import { useAppSelector } from "../../../../services/redux/hooks/use-selector";
import { selectUserArtifactGroup } from "../../../Auth/redux/selectors";
import { UploadFileButton } from "../../../../common/Components/Buttons/UploadFileButton";
import { useAppModals } from "../../../../app/state/modalSlice/hook/use-app-modals";
import styles from "./styles.module.css";

interface Props {
  storeData: TStoreData | null;
  className?: string;
}

export const FileContainer = (props: Props) => {
  const {
    info,
    anchorPopoverEl,
    openPopover,
    handleOpenPopover,
    handleClosePopover,
  } = usePopover();
  const { handleCreateStoreFile, handleClick, isFileLoading, deleteFile } =
    useAddDatabaseFile();
  const artifacts = useAppSelector(selectUserArtifactGroup);
  const { anchorEl, open, handleMenu, handleMenuClose } = useMenu();
  const { handleOpenModal, handleCloseModal } = useAppModals();
  const { t } = useTranslation();

  return (
    <div className={styles["file-container"]}>
      <AppUnderlinedTitle
        className={styles.title}
        leftContent={t("knowledge_base.title")}
      >
        {artifacts ? (
          <AppButton
            buttonType={"text"}
            icon={<PlusIcon />}
            onClick={handleMenu}
          >
            {t("knowledge_base.upload_button")}
          </AppButton>
        ) : (
          <UploadFileButton
            type={"button"}
            title={t("knowledge_base.upload_button")}
            handleFileUpload={handleCreateStoreFile}
            icon={<PlusIcon />}
          />
        )}
      </AppUnderlinedTitle>
      {props.storeData?.files?.length ? (
        <ul className={styles["file-list"]}>
          {props.storeData?.files?.map((file) => (
            <li key={file.id} className={styles["file-list-item"]}>
              <div className={styles["file-name-container"]}>
                <PaperclipIcon />
                <span>{file?.filename}</span>
              </div>
              {file.isLoading ? (
                <AppLoading
                  settingSize={16}
                  className={styles["file-loader"]}
                />
              ) : (
                <AppIconButton
                  onClick={(e) =>
                    handleOpenPopover(e, { id: file.id, value: file.filename })
                  }
                >
                  <TrashIcon />
                </AppIconButton>
              )}
              <AppPopover
                open={openPopover}
                anchorEl={anchorPopoverEl}
                handleClose={handleClosePopover}
                onOk={() => {
                  deleteFile(info?.id ?? "");
                  handleClosePopover();
                }}
              >
                {t("versioning.delete_popover")}
                <strong>{info?.value}</strong>?
              </AppPopover>
            </li>
          ))}
        </ul>
      ) : (
        <p className={styles.placeholder}>{t("knowledge_base.no_files")}</p>
      )}
      {isFileLoading && <AppLoading small />}
      <AttachedFileMenu
        open={open}
        anchorEl={anchorEl}
        onClose={handleMenuClose}
        handleUploadFile={(e) => {
          handleCreateStoreFile(e);
          handleMenuClose();
        }}
        openAttachedFileModal={() => {
          handleOpenModal("attached_file_to_database_modal", null);
          handleMenuClose();
        }}
        multiple
      />
      <AttachedFileToDatabaseModal
        extensions={UPLOADED_FILE_EXTENSIONS}
        onClose={handleCloseModal}
        handleClick={handleClick}
        multiple
      />
    </div>
  );
};
